import React, { useState, useMemo, useRef, useEffect } from "react";
import useSWR from "swr";
import { URLS } from "../../services/urls";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBriefcase, faMapMarkerAlt, faSearch, faChevronDown } from "@fortawesome/free-solid-svg-icons";

import HomeBanner from "./components/BannerSlider";
import OldJob from "./components/OldJob";
import JobTabContent from "./components/JobTabContent";
import PopCategory from "./components/PopCategory";
import JobsByLocation from "./components/JobsByLocation";
import JobProcess from "./components/JobProcess";
import NewsLetter from "../../components/newsletter";

import { API } from "../../services/axios";
import { getAvailableCategories, hasOpenedJobs } from "../../utils/fonctions";
import "./Home.css"; // Nouveau fichier CSS

const fetcher = (url) =>
  API.get(url).then((res) => {
    return res.data.results.filter((el) =>
      [
        "Douala", "Yaoundé", "Bafoussam", "Garoua", "Maroua",
        "Bamenda", "Buea", "Bertoua", "Kribi",
      ].includes(el.name)
    );
  });

const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // State
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(null);
  const [location, setLocation] = useState(null);
  const [showIndustries, setShowIndustries] = useState(false);
  const [showLocation, setShowLocation] = useState(false);

  // Refs pour gérer la fermeture des dropdowns au clic extérieur
  const industryRef = useRef(null);
  const locationRef = useRef(null);

  // Click outside handler
  useEffect(() => {
    function handleClickOutside(event) {
        if (industryRef.current && !industryRef.current.contains(event.target)) {
            setShowIndustries(false);
        }
        if (locationRef.current && !locationRef.current.contains(event.target)) {
            setShowLocation(false);
        }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  // Fetch data
  const { data: categories, isLoading: loadingCategories } = useSWR(
    URLS.CONFIGS.PROFILE.SECTOR.list("", 1500)
  );
  const { data: locations, isLoading: loadingLocations } = useSWR(
    URLS.CONFIGS.PROFILE.COUNTRIES.CITIES.cmr(""),
    fetcher
  );

  // Compute categories
  const displayedCategories = useMemo(() => {
    if (!categories?.results) return [];
    return _.orderBy(
      categories.results.filter((cat) => hasOpenedJobs(cat)),
      [(cat) => cat.jobs.length],
      ["desc"]
    ).slice(0, 7);
  }, [categories]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (category?.id) params.set("industry", category.id);
    if (location?.name) params.set("location", location.name);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <>
      {/* Hero & Filters */}
      <section className="section-box mt-95">
        <div className="banner-hero hero-2">
          <div className="banner-inner">
            <div className="block-banner">
              
              <h1
                className="text-42 color-white wow animate__animated animate__fadeInUp mb-15"
                dangerouslySetInnerHTML={{ __html: t("textJobBoard") }}
              ></h1>
              
              <div
                className="font-lg font-regular color-white mt-20 wow animate__animated animate__fadeInUp"
                data-wow-delay=".1s"
              >
                {/* Sous-titre optionnel ici */}
              </div>

              {/* NOUVELLE BARRE DE RECHERCHE MODERNE */}
              <div
                className="form-find mt-40 wow animate__animated animate__fadeIn"
                data-wow-delay=".2s"
              >
                <form onSubmit={handleSubmit} className="modern-search-bar">
                    
                    {/* 1. Industry Dropdown */}
                    <div className="search-group" ref={industryRef}>
                        <div 
                            className="search-input-wrapper cursor-pointer"
                            onClick={() => setShowIndustries(!showIndustries)}
                        >
                            <FontAwesomeIcon icon={faBriefcase} className="search-icon text-muted" />
                            <div className="flex-grow-1 ms-2">
                                <span className="label-small text-muted d-block">{t("textIndustry")}</span>
                                <span className="value-text text-dark fw-bold text-truncate d-block" style={{maxWidth: '150px'}}>
                                    {category?.name ?? "Tout secteur"}
                                </span>
                            </div>
                            <FontAwesomeIcon icon={faChevronDown} className={`chevron-icon ${showIndustries ? 'rotate' : ''}`} />
                        </div>
                        
                        {/* Dropdown Menu */}
                        {showIndustries && (
                            <div className="custom-dropdown-menu shadow-lg">
                                <div className="dropdown-item" onClick={() => { setCategory(null); setShowIndustries(false); }}>
                                    Tout secteur
                                </div>
                                {categories?.results?.map((cat) => (
                                    <div
                                        className={`dropdown-item ${category?.id === cat.id ? "active" : ""}`}
                                        key={cat.id}
                                        onClick={() => {
                                            setCategory(cat);
                                            setShowIndustries(false);
                                        }}
                                    >
                                        {cat.name}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="divider d-none d-md-block"></div>

                    {/* 2. Location Dropdown */}
                    <div className="search-group" ref={locationRef}>
                        <div 
                            className="search-input-wrapper cursor-pointer"
                            onClick={() => setShowLocation(!showLocation)}
                        >
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="search-icon text-muted" />
                            <div className="flex-grow-1 ms-2">
                                <span className="label-small text-muted d-block">{t("textLocation")}</span>
                                <span className="value-text text-dark fw-bold text-truncate d-block" style={{maxWidth: '150px'}}>
                                    {location?.name ?? "Toute ville"}
                                </span>
                            </div>
                            <FontAwesomeIcon icon={faChevronDown} className={`chevron-icon ${showLocation ? 'rotate' : ''}`} />
                        </div>

                        {/* Dropdown Menu */}
                        {showLocation && (
                            <div className="custom-dropdown-menu shadow-lg">
                                <div className="dropdown-item" onClick={() => { setLocation(null); setShowLocation(false); }}>
                                    Toute ville
                                </div>
                                {locations?.map((loc) => (
                                    <div
                                        className={`dropdown-item ${location?.id === loc.id ? "active" : ""}`}
                                        key={loc.id}
                                        onClick={() => {
                                            setLocation(loc);
                                            setShowLocation(false);
                                        }}
                                    >
                                        {loc.name}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="divider d-none d-md-block"></div>

                    {/* 3. Keyword Input */}
                    <div className="search-group flex-grow-1">
                        <div className="search-input-wrapper">
                            <FontAwesomeIcon icon={faSearch} className="search-icon text-muted" />
                            <input
                                className="form-control border-0 shadow-none bg-transparent fw-bold"
                                type="text"
                                placeholder={t("textkeyword") || "Mots-clés..."}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* 4. Submit Button */}
                    <div className="search-action p-2">
                        <button type="submit" className="btn btn-gradient text-white fw-bold px-4 py-3 h-100 w-100 w-md-auto">
                            {t("textSearch")}
                        </button>
                    </div>

                </form>
              </div>

              {/* Popular Tags */}
              <div
                className="list-tags-banner mt-20 wow animate__animated animate__fadeInUp"
                data-wow-delay=".3s"
              >
                <strong className="text-white me-2">{t("textpop")}:</strong>
                <span className="text-white opacity-75">Designer, Web, IOS, PHP, Senior...</span>
              </div>
            </div>
          </div>
          
          <div className="mt-60">
            {!loadingCategories && (
              <HomeBanner categories={categories?.results ? getAvailableCategories(categories?.results) : []} />
            )}
          </div>
        </div>
      </section>

      {/* All Jobs Slider */}
      <OldJob />

      {/* Day Job Section */}
      <section className="section-box mt-30">
        <div className="container">
          <div className="text-start">
            <h2 className="section-title text mb-10 wow animate__animated animate__fadeInUp">
              {t("dayJob")}
            </h2>
            <p className="font-lg text-danger color-text-paragraph-2 wow animate__animated animate__fadeInUp">
              {t("findJob")}
            </p>
            {loadingCategories && (
              <div className="d-flex my-5">
                <div className="spinner-border m-auto text-primary" role="status"></div>
              </div>
            )}
          </div>

          {/* Tabs Navigation */}
          {!loadingCategories && (
          <div className="list-tabs mt-40">
            <ul className="nav nav-tabs" role="tablist">
                {displayedCategories.map((cat, idx) => (
                  <li key={cat.id} className="nav-item">
                    <a
                      className={idx === 0 ? "nav-link active" : "nav-link"}
                      id={`nav-tab-job-${cat.id}`}
                      href={`#tab-job-${cat.id}`}
                      data-bs-toggle="tab"
                      role="tab"
                      aria-controls={`tab-job-${cat.id}`}
                      aria-selected={idx === 0}
                    >
                      {cat.name} <span className="badge bg-light text-dark ms-1 rounded-pill">{cat.jobs.length}</span>
                    </a>
                  </li>
                ))}
            </ul>
          </div>
          )}

          {/* Tabs Content */}
          <div className="mt-50">
            <div className="tab-content" id="myTabContent-1">
              {!loadingCategories &&
                displayedCategories.map((cat, idx) => (
                  <div
                    key={cat.id}
                    className={`tab-pane fade ${idx === 0 ? "show active" : ""}`}
                    id={`tab-job-${cat.id}`}
                    role="tabpanel"
                    aria-labelledby={`nav-tab-job-${cat.id}`}
                  >
                    <JobTabContent jobs={cat.jobs} category={cat.id} />
                  </div>
                ))}
            </div>
          </div>
        </div>
      </section>

      {/* Other Sections */}
      {/* <PopCategory/> */}
      <JobsByLocation locations={locations ?? []} />
      <JobProcess />
      <NewsLetter />
    </>
  );
};

export default HomePage;